import React, { useMemo, useState } from "react";
import {
  Box,
  Switch,
  Tooltip,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { NEUTRAL, RADIUS } from "../../styles/brandTokens";
import { INTEGRATION_CATEGORIES, IntegrationCategoryId } from "../../utils/connectorUtils";
import { ActiveConnection, groupConnectionsByCustomer } from "./integrationsModel";

interface IntegrationsActiveProps {
  connections: ActiveConnection[];
  /** Row click: opens the connection's configuration. Never mutates. */
  onOpen: (connection: ActiveConnection) => void;
  /** The switch only. Separated from `onOpen` so inspecting cannot disable. */
  onToggle: (connection: ActiveConnection) => void;
  /** The connection currently saving, if any. */
  togglingKey?: string | null;
  /** Prefills the search box, e.g. when arriving from a Directory card's connected count. */
  initialSearch?: string;
}

const CATEGORY_TITLE: Record<string, string> = Object.fromEntries(
  INTEGRATION_CATEGORIES.map((category) => [category.id, category.title])
);

/**
 * Live connections as a scannable table, grouped MSP-wide first then by customer.
 * A table rather than cards: this is the view you open to answer "is X still
 * connected for Y", which is a lookup, not browsing.
 */
const IntegrationsActive: React.FC<IntegrationsActiveProps> = ({
  connections,
  onOpen,
  onToggle,
  togglingKey,
  initialSearch = "",
}) => {
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState<IntegrationCategoryId | "all">("all");

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = connections.filter((connection) => {
      if (category !== "all" && connection.category !== category) return false;
      if (!needle) return true;
      return (
        connection.name.toLowerCase().includes(needle) ||
        (connection.customer?.name || "").toLowerCase().includes(needle)
      );
    });
    return groupConnectionsByCustomer(filtered);
  }, [connections, search, category]);

  /** Only offer categories that have connections; the full taxonomy lives in Directory. */
  const categoryOptions = useMemo(() => {
    const present = new Set(connections.map((connection) => connection.category));
    return INTEGRATION_CATEGORIES.filter((entry) => present.has(entry.id) || entry.id === category);
  }, [connections, category]);

  const total = groups.reduce((sum, group) => sum + group.connections.length, 0);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Search connections…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: "1 1 280px", maxWidth: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: NEUTRAL.textMuted }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={category}
            onChange={(event) => setCategory(event.target.value as IntegrationCategoryId | "all")}
            displayEmpty
          >
            <MenuItem value="all">All categories</MenuItem>
            {categoryOptions.map((entry) => (
              <MenuItem key={entry.id} value={entry.id}>
                {entry.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {total === 0 ? (
        <Box
          sx={{
            border: `1px solid ${NEUTRAL.border}`,
            borderRadius: `${RADIUS.card}px`,
            p: 4,
            textAlign: "center",
            bgcolor: NEUTRAL.surface,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: NEUTRAL.text, mb: 0.5 }}>
            No active connections match
          </Typography>
          <Typography sx={{ fontSize: 13, color: NEUTRAL.textSecondary }}>
            Clear the filters, or connect something from the Directory tab.
          </Typography>
        </Box>
      ) : (
        groups.map((group) => (
          <Box key={group.id} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text }}>
                {group.title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: NEUTRAL.textMuted }}>
                {group.subtitle} · {group.connections.length}
              </Typography>
            </Box>
            <Box
              sx={{
                border: `1px solid ${NEUTRAL.border}`,
                borderRadius: `${RADIUS.card}px`,
                overflow: "hidden",
                bgcolor: NEUTRAL.surface,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: NEUTRAL.subtle }}>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NEUTRAL.textSecondary, letterSpacing: "0.04em" }}>
                      INTEGRATION
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700, color: NEUTRAL.textSecondary, letterSpacing: "0.04em" }}>
                      CATEGORY
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontSize: 11, fontWeight: 700, color: NEUTRAL.textSecondary, letterSpacing: "0.04em", width: 120 }}
                    >
                      ENABLED
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.connections.map((connection) => (
                    <TableRow
                      key={connection.key}
                      hover
                      onClick={() => onOpen(connection)}
                      sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            component="img"
                            src={connection.logo}
                            alt=""
                            sx={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0 }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: NEUTRAL.text }}>
                              {connection.name}
                            </Typography>
                            <Typography
                              noWrap
                              sx={{ fontSize: 12, color: NEUTRAL.textSecondary, maxWidth: 420 }}
                            >
                              {connection.description}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: NEUTRAL.textSecondary }}>
                          {CATEGORY_TITLE[connection.category] || connection.category}
                        </Typography>
                      </TableCell>
                      {/* stopPropagation is the whole point: without it the switch
                          would also fire the row's navigation. */}
                      <TableCell align="right" onClick={(event) => event.stopPropagation()} sx={{ cursor: "default" }}>
                        <Tooltip
                          title={connection.enabled ? "Disable this connection" : "Enable this connection"}
                        >
                          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75 }}>
                            <Switch
                              size="small"
                              checked={connection.enabled}
                              disabled={togglingKey === connection.togglingKey}
                              onChange={() => onToggle(connection)}
                              inputProps={{
                                "aria-label": `${connection.enabled ? "Disable" : "Enable"} ${connection.name}`,
                              }}
                            />
                            <Typography sx={{ fontSize: 12, color: NEUTRAL.textSecondary }}>
                              {connection.enabled ? "On" : "Off"}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default IntegrationsActive;
